import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import ColumnDivider from '../components/ColumnDivider'
import VScrollArea from '../components/VScrollArea'
import { useVariantLensApiClient } from '../features/auth/hooks/useVariantLensApiClient'
import ActiveFilterChips from '../features/variants/ActiveFilterChips'
import FilterSidebar from '../features/variants/FilterSidebar'
import { locusToRegion, variantIdToLocus, type GoToResult } from '../features/variants/goTo'
import LocusListPanel from '../features/variants/LocusListPanel'
import { isCnv, variantLocus, variantRoiLocus, type Variant } from '../features/variants/types'
import { decodeState, encodeState } from '../features/variants/urlState'
import { useCaseFacets } from '../features/variants/useCaseFacets'
import { useCaseVariants } from '../features/variants/useCaseVariants'
import { useCaseVariantTracks } from '../features/variants/useCaseVariantTracks'
import VariantDetailPanel from '../features/variants/VariantDetailPanel'
import VariantNotFoundPanel from '../features/variants/VariantNotFoundPanel'
import {
  applyFilters,
  computeFacetCounts,
  emptyFilters,
  sortVariants,
  type SortKey,
  type VariantFilters,
  type VariantSort,
} from '../features/variants/variantQuery'
import VariantTable from '../features/variants/VariantTable'
import IgvMount, { type IgvReference, type IgvTrack } from '../features/viewers/igv/IgvMount'
import { DEFAULT_IGV_SETTINGS, toIgvTrack, type ApiTrack, type IgvDisplaySettings } from './igvTracks'

type ApiReference = {
  id: string
  fastaUrl: string
  indexUrl: string
}

/**
 * Human-readable name for the custom IGV reference, keyed on the API reference
 * id. Both are GRCh38; the distinction is which build (the LSMC CRAMs use the
 * no-alt analysis set, GEN-5234). Falls back to a generic GRCh38 label.
 */
function referenceDisplayName(id: string): string {
  switch (id) {
    case 'hg38_no_alt':
      return 'GRCh38 (no-alt)'
    case 'hg38_broad':
      return 'GRCh38 (Broad)'
    default:
      return 'GRCh38'
  }
}

type TracksResponse = {
  sample_id?: string
  case_id?: string
  order_id?: string
  tracks: ApiTrack[]
  reference?: ApiReference | null
}

// Resizable side panels; the IGV center column flexes to fill whatever is left.
// The right (variant table) default is kept modest so IGV gets the bulk of the
// width by default; reviewers can still drag it wider up to RIGHT_PANEL_MAX.
const RIGHT_PANEL_DEFAULT = 360
const RIGHT_PANEL_MIN = 320
const RIGHT_PANEL_MAX = 760
const LEFT_PANEL_DEFAULT = 210
const LEFT_PANEL_MIN = 160
const LEFT_PANEL_MAX = 420

// Minimum width the IGV center column keeps: the side panels are never allowed
// to crowd it below this, so IGV stays wide and usable even on smaller windows
// (GEN-5698). Enforced in the grid track (minmax) and when clamping drags.
const IGV_MIN_WIDTH = 560

// Persist the two panel widths so a preferred layout survives navigation and
// reload (GEN-5696). Shared across case and order mode (one component), so one
// layout applies everywhere; without this every mount reset to the defaults and
// left the IGV column inconsistently narrow with no way back but re-dragging.
const PANEL_WIDTHS_KEY = 'variant-lens.panelWidths'

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

// Rehydrate a stored width, clamped to [min, max] so a stale or hand-edited
// value can't produce an unusable layout; falls back to the default when there
// is nothing stored, the value isn't a finite number, or storage is
// unavailable (private mode / SSR).
function loadPanelWidth(key: 'left' | 'right', def: number, min: number, max: number): number {
  try {
    const raw = window.localStorage.getItem(PANEL_WIDTHS_KEY)
    if (!raw) return def
    const parsed = JSON.parse(raw) as unknown
    if (typeof parsed !== 'object' || parsed === null) return def
    const value = (parsed as Record<string, unknown>)[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) return def
    return clamp(value, min, max)
  } catch {
    return def
  }
}

function savePanelWidths(left: number, right: number): void {
  try {
    window.localStorage.setItem(PANEL_WIDTHS_KEY, JSON.stringify({ left, right }))
  } catch {
    // localStorage may be unavailable (private mode, SSR); silently ignore.
  }
}

// Refresh presigned track URLs at most this often (GEN-5243), so a genuinely
// broken URL can't drive a refetch loop.
const REFRESH_MIN_INTERVAL_MS = 30_000

// On tab refocus, only refetch presigns if they are at least this old (< the
// API's 6h presign TTL), so we refresh before a 403 without remounting IGV on
// every quick alt-tab. 5h.
const TRACK_STALE_MS = 5 * 60 * 60 * 1000

// How often a *foreground* tab checks whether its presigns have crossed
// TRACK_STALE_MS (GEN-5243). A tab left visible past the TTL never fires
// visibilitychange, so this poll is the only pre-emptive refresh for it. The
// check is cheap and the actual refetch is still gated by TRACK_STALE_MS and
// rate-limited by refreshTracks, so a tight-ish interval costs nothing extra.
const FOREGROUND_POLL_MS = 5 * 60 * 1000

/**
 * Heuristic: does an IGV error look like an expired/forbidden presigned URL
 * (GEN-5243) rather than a real data problem? S3 returns 403 Access Denied /
 * Forbidden once a presign expires. Scoped to those S3-auth signals; generic
 * words like "expired"/"signature" are intentionally excluded to avoid
 * false-positive remounts on unrelated igv.js errors.
 */
function looksLikeExpiredUrl(message: string): boolean {
  return /\b(403|forbidden|access denied)\b/i.test(message)
}

/**
 * RefSeq gene model from igv.org's public, CORS-enabled GRCh38 bundle (GEN-3110).
 * Rendered above the sample tracks so reviewers see gene/exon structure (like
 * tgg-viewer) regardless of whether the case uses a named genome or our custom
 * Broad reference - both are GRCh38 with chr-prefixed contigs, so coordinates
 * line up. `visibilityWindow: -1` keeps it visible at every zoom level.
 */
const REFSEQ_GENES_TRACK: IgvTrack = {
  name: 'RefSeq Genes',
  format: 'refgene',
  url: 'https://s3.amazonaws.com/igv.org.genomes/hg38/refGene.sorted.txt.gz',
  indexURL: 'https://s3.amazonaws.com/igv.org.genomes/hg38/refGene.sorted.txt.gz.tbi',
  order: -10,
  visibilityWindow: -1,
}

/**
 * UCSC segmental-duplication regions (hg38 genomicSuperDups), shipped as a plain
 * (unindexed) BED under the app's own origin. igv.js loads it once and filters
 * client-side, so it needs no CORS, presign, or Range requests, avoiding the
 * BGZF-vs-Content-Encoding conflict where the dev/preview server gzips a
 * .bed.gz and the browser transparently inflates it before igv.js can range in.
 * Lets a reviewer confirm whether a CNV boundary overlaps a known segdup, the
 * second half of the SOP's segdup-rejection criteria for gains/losses
 * (GEN-4384). The BED `score` column is percent-identity x 1000; `useScore`
 * shades higher-identity paralogs darker, with min/max narrowed to the segdup
 * identity band (90-100%) so the gradient is visible rather than all near-opaque.
 * Off by default; only relevant during CNV review.
 */
const SEGDUP_TRACK: IgvTrack = {
  name: 'Segmental Dups',
  format: 'bed',
  url: `${import.meta.env.BASE_URL}igv/genomicSuperDups.hg38.bed`,
  color: 'rgb(153,51,153)',
  useScore: true,
  min: 900,
  max: 1000,
  order: -9,
  visibilityWindow: -1,
}

/**
 * Props that specialize the shared workspace for a case (sample id) vs an order
 * (SeqOne analysis token). The data-fetching contract is identical across both
 * (GEN-5495 parameterized the variant hooks); this just supplies the per-mode
 * paths and labels so CasePage and OrderCasePage share one implementation
 * (GEN-5569).
 */
export type CaseWorkspaceProps = {
  /** The case id or order token; used for the title, LocusList key, and hooks. */
  displayId: string
  /** 'case' | 'order' - drives labels and whether the family toggle applies. */
  kind: 'case' | 'order'
  /**
   * Resource prefix for the variant hooks (variants/facets/variant-tracks).
   * Undefined in case mode so the hooks default to `/cases/{id}`; in order mode
   * this is `/orders/{token}` (already encoded, no trailing slash).
   */
  basePath?: string
  /** Full path to the IGV tracks endpoint for this entity (already encoded). */
  tracksPath: string
  /**
   * Builds the single-variant deep-link lookup path for a variant id, or null
   * when the mode has no single-variant endpoint (deep-link then falls back to
   * loaded rows only). Case mode uses `/variants/{id}?sample_id=`; order mode
   * uses `/orders/{token}/variants/{id}`.
   */
  variantLookupPath: ((variantId: string, track?: string) => string) | null
  /** Back-link target and label for the header. */
  backTo: string
  backLabel: string
  /** Uppercase label above the title ("Case" / "Order"). */
  titleKicker: string
  /**
   * Optional short badge shown beside the title (e.g. "Trio analysis" derived
   * from a SeqOne analysis display name, GEN-5657). Purely informational; it
   * does not change what tracks load. Omitted when there is nothing to show.
   */
  subtitle?: string
}

const CaseWorkspace: React.FC<CaseWorkspaceProps> = ({
  displayId,
  kind,
  basePath,
  tracksPath,
  variantLookupPath,
  backTo,
  backLabel,
  titleKicker,
  subtitle,
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const apiClient = useVariantLensApiClient()

  // The family toggle is a case-only concept (a sample can be shown with or
  // without its family); an order already delivers its full member set, so the
  // toggle is fixed on for orders.
  const includeFamily = kind === 'order' ? true : searchParams.get('family') !== '0'

  // --- IGV tracks (existing behavior) ---
  const [tracksLoading, setTracksLoading] = useState(true)
  const [tracks, setTracks] = useState<ApiTrack[] | null>(null)
  const [reference, setReference] = useState<ApiReference | null>(null)
  const [tracksError, setTracksError] = useState<string | null>(null)
  const [showGenes, setShowGenes] = useState(true)
  const [showSegdup, setShowSegdup] = useState(false)
  const [igvSettings, setIgvSettings] = useState<IgvDisplaySettings>(DEFAULT_IGV_SETTINGS)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [igvLocus, setIgvLocus] = useState<string | undefined>(undefined)
  const [rightWidth, setRightWidth] = useState(() =>
    loadPanelWidth('right', RIGHT_PANEL_DEFAULT, RIGHT_PANEL_MIN, RIGHT_PANEL_MAX)
  )
  const [leftWidth, setLeftWidth] = useState(() =>
    loadPanelWidth('left', LEFT_PANEL_DEFAULT, LEFT_PANEL_MIN, LEFT_PANEL_MAX)
  )

  // Persist the widths so the layout survives navigation and reload (GEN-5696).
  // A drag updates state on each pointermove, so this writes repeatedly during a
  // drag; localStorage writes are cheap and synchronous, and the final write is
  // the one that matters, so debouncing would add complexity for no real gain.
  useEffect(() => {
    savePanelWidths(leftWidth, rightWidth)
  }, [leftWidth, rightWidth])

  // Presigned track URLs expire (GEN-5243). Bumping this nonce re-runs the
  // fetch effect below to get fresh presigns; guarded so we refresh at most
  // once per REFRESH_MIN_INTERVAL_MS to avoid loops on a genuinely broken URL.
  const [refreshNonce, setRefreshNonce] = useState(0)
  const lastRefreshRef = useRef(0)
  const tracksLoadedAtRef = useRef(0)
  // The (displayId, includeFamily) the current tracks are being shown or loaded
  // for. Claimed synchronously when a real load starts (see below), so a bumped
  // refreshNonce only means "refresh in place" when this key still matches; a
  // case/family switch is always a full load even if a refresh happened earlier,
  // so the refresh path can't outlive the case it was minted for (Bugbot).
  const tracksKeyRef = useRef<string | null>(null)
  const refreshTracks = useCallback(() => {
    const now = Date.now()
    if (now - lastRefreshRef.current < REFRESH_MIN_INTERVAL_MS) return
    lastRefreshRef.current = now
    setRefreshNonce((n) => n + 1)
  }, [])

  useEffect(() => {
    if (!displayId) {
      setTracksLoading(false)
      setTracksError('No case ID provided')
      return
    }
    const abort = new AbortController()
    // A run is an in-place presign refresh only when the nonce was bumped AND we
    // are still on the same case/family the current tracks belong to. Any
    // displayId/includeFamily change is a real load regardless of refreshNonce,
    // so a prior refresh can't make a new case keep the old case's tracks, skip
    // loading, or swallow fetch errors (Bugbot). On a refresh we keep the current
    // tracks visible so IGV isn't blanked/remounted mid-review; new presigns
    // still propagate via setTracks(body.tracks) on success.
    const loadKey = `${displayId}:${includeFamily ? '1' : '0'}`
    const isRefresh = refreshNonce > 0 && tracksKeyRef.current === loadKey
    setTracksLoading(!isRefresh)
    setTracksError(null)
    if (!isRefresh) {
      // Claim the key synchronously as we start (and clear) a real load, not on
      // success: otherwise a pending/failed load to another case leaves the ref
      // pointing at the prior case, and navigating back while refreshNonce is
      // still raised would be mistaken for an in-place refresh even though the
      // tracks were already cleared (Bugbot).
      tracksKeyRef.current = loadKey
      setTracks(null)
      setReference(null)
    }
    const url = kind === 'order' ? tracksPath : `${tracksPath}?include_family=${includeFamily ? 'true' : 'false'}`
    apiClient
      .get(url, undefined, true, abort.signal)
      .then(async (response) => {
        if (response.status === 404) throw new Error(`${titleKicker} '${displayId}' not found`)
        if (!response.ok) throw new Error(`API returned ${response.status}`)
        const body = (await response.json()) as TracksResponse
        setTracks(body.tracks)
        setReference(body.reference ?? null)
        tracksLoadedAtRef.current = Date.now()
      })
      .catch((e: unknown) => {
        if (abort.signal.aborted) return
        // A failed refresh must not leave the viewer stuck: free the rate-limit
        // window so the next trigger can retry, and only surface an error on the
        // initial load (a refresh keeps the last-good tracks on screen).
        if (isRefresh) {
          lastRefreshRef.current = 0
        } else {
          setTracksError(e instanceof Error ? e.message : 'Failed to load tracks')
        }
      })
      .finally(() => {
        if (!abort.signal.aborted && !isRefresh) setTracksLoading(false)
      })
    return () => abort.abort()
  }, [displayId, apiClient, includeFamily, refreshNonce, kind, tracksPath, titleKicker])

  // Presigned URLs expire (6h TTL, and cannot outlive the signing task-role
  // creds), so IGV's byte-range fetches on large CRAMs can 403 on long-open
  // tabs (GEN-5243). Three reactive refreshes keep long sessions working without
  // remounting IGV on a fixed timer:
  //   (1) onIgvError: IgvMount only reports errors from browser (re)creation,
  //       so this catches an expired presign at (re)mount time, not igv.js's
  //       lazy pan/zoom Range 403s (those never reach onError).
  //   (2) visibilitychange: when a backgrounded tab returns to view past a
  //       staleness threshold, pre-emptively refetch so the next interaction
  //       uses fresh presigns.
  //   (3) foreground poll: a tab left visible past the TTL never fires (2) and
  //       may not fire (1) until the user pans, so poll on FOREGROUND_POLL_MS to
  //       refresh once presigns cross the staleness threshold.
  // All three funnel through refreshTracks, which rate-limits itself, and (2)/(3)
  // additionally gate on presign age so they never refetch fresh presigns.
  const onIgvError = useCallback(
    (message: string) => {
      if (looksLikeExpiredUrl(message)) refreshTracks()
    },
    [refreshTracks]
  )

  // Refresh only when the current presigns are old enough to plausibly have
  // expired; a quick alt-tab (or an early poll tick) must not remount IGV. Uses
  // TRACK_STALE_MS (< the 6h presign TTL) so we refresh before, not after, a 403.
  const refreshIfStale = useCallback(() => {
    if (tracksLoadedAtRef.current && Date.now() - tracksLoadedAtRef.current > TRACK_STALE_MS) {
      refreshTracks()
    }
  }, [refreshTracks])

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      refreshIfStale()
    }
    document.addEventListener('visibilitychange', onVisible)
    // Cover a tab left visible past the TTL, which visibilitychange never sees.
    const poll = setInterval(() => {
      if (document.visibilityState === 'visible') refreshIfStale()
    }, FOREGROUND_POLL_MS)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      clearInterval(poll)
    }
  }, [refreshIfStale])

  const igvTracks = useMemo<IgvTrack[] | undefined>(() => {
    if (!tracks) return undefined
    const sampleTracks = tracks.map((t) => toIgvTrack(t, igvSettings))
    return [...(showGenes ? [REFSEQ_GENES_TRACK] : []), ...(showSegdup ? [SEGDUP_TRACK] : []), ...sampleTracks]
  }, [tracks, showGenes, showSegdup, igvSettings])
  const igvReference = useMemo<IgvReference | undefined>(
    () =>
      reference
        ? {
            id: reference.id,
            name: referenceDisplayName(reference.id),
            fastaURL: reference.fastaUrl,
            indexURL: reference.indexUrl,
          }
        : undefined,
    [reference]
  )

  // URL-derived workspace state (filters, sort, selection, track).
  const { filters, sort, selectedVariantId, track: urlTrack } = useMemo(() => decodeState(searchParams), [searchParams])

  // Selectable variant VCFs (e.g. small-variant vs CNV). The selector is hidden
  // when a sample has a single track. The URL `track` is validated against the
  // discovered tracks so a stale ?track= (from another case, or a single-track
  // sample) can't drive the fetch against a VCF this sample doesn't have; we
  // fall back to the default until the value is proven valid. `queryTrack` is
  // sent to the API only for a known non-default track (URL stays clean for the
  // default); the select still displays the default as the active option.
  const {
    tracks: variantTracks,
    defaultTrackId,
    loading: variantTracksLoading,
  } = useCaseVariantTracks(displayId, basePath)
  const isKnownTrack = urlTrack !== null && variantTracks.some((t) => t.track_id === urlTrack)
  const selectedTrack = (isKnownTrack ? urlTrack : defaultTrackId) ?? ''
  // Only send ?track= for a known, non-default track; the default is implicit.
  const queryTrack = isKnownTrack && urlTrack !== defaultTrackId ? urlTrack : undefined
  // When the URL pins a track, wait for the track list before loading variants
  // so a valid ?track= isn't first queried as the default (and an invalid one
  // is dropped first). With no URL track, load the default immediately.
  const trackResolved = urlTrack === null || !variantTracksLoading
  // The single-variant deep-link fetch needs a stricter gate than the list: a
  // stale (unknown) ?track= is about to be stripped along with the selection, so
  // firing now would query the default VCF for a variant that may belong to
  // another track. Only fetch once the track is null or proven valid (GEN-3597).
  const deepLinkTrackReady = urlTrack === null || isKnownTrack

  // --- Variants ---
  // A locus/interval entered in the go-to box scopes the table to that window
  // (so every variant there is listed) and takes precedence over the chrom
  // filter. Otherwise a single selected chromosome scopes the fetch so the
  // table jumps straight to that contig instead of paging through chr1.
  const [gotoRegion, setGotoRegion] = useState<string | null>(null)
  const [pendingLocusSelect, setPendingLocusSelect] = useState(false)
  const region = gotoRegion ?? (filters.chroms.length === 1 ? filters.chroms[0] : undefined)
  // TAG and FILTER are pushed to the server so the table reflects whole-file
  // facet counts, not just the loaded page (GEN-3149). chrom/locus is handled by
  // `region`; numeric filters (QUAL/DP/VAF) stay client-side in applyFilters.
  const { variants, loading, initialLoading, error, hasMore, loadMore } = useCaseVariants(
    trackResolved ? displayId : undefined,
    undefined,
    region,
    filters.tags,
    filters.filters,
    queryTrack,
    basePath
  )

  const updateUrl = useCallback(
    (next: {
      filters?: VariantFilters
      sort?: VariantSort
      selectedVariantId?: string | null
      track?: string | null
    }) => {
      setSearchParams(
        (prev) => {
          const current = decodeState(prev)
          return encodeState(
            {
              filters: next.filters ?? current.filters,
              sort: next.sort ?? current.sort,
              selectedVariantId:
                next.selectedVariantId !== undefined ? next.selectedVariantId : current.selectedVariantId,
              track: next.track !== undefined ? next.track : current.track,
            },
            prev
          )
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  // Once the track list resolves (loaded or failed), normalize ?track=: drop an
  // unknown/stale value -- including when the list failed to load -- along with
  // the now-meaningless selection, and drop a value that merely equals the
  // default so the default stays implicit and the URL stays shareable.
  useEffect(() => {
    if (urlTrack === null || variantTracksLoading) return
    if (!isKnownTrack) {
      updateUrl({ track: null, selectedVariantId: null })
    } else if (urlTrack === defaultTrackId) {
      updateUrl({ track: null })
    }
  }, [urlTrack, variantTracksLoading, isKnownTrack, defaultTrackId, updateUrl])

  const filteredSorted = useMemo(() => sortVariants(applyFilters(variants, filters), sort), [variants, filters, sort])
  // Prefer precomputed whole-case counts; fall back to page-local tallies when
  // the server sidecar isn't available yet. Facets follow the selected track so
  // the CNV track surfaces its own TAG vocabulary and counts (GEN-4325). Gate on
  // trackResolved like the variants list: while a pinned ?track= is still being
  // validated, queryTrack is not yet set, so firing now would fetch (and briefly
  // show as authoritative) default-track facets for a non-default URL.
  const {
    facets: serverFacets,
    tagOrder: serverTagOrder,
    tagDefinitions: serverTagDefinitions,
    track: facetsTrack,
    loaded: facetsLoaded,
  } = useCaseFacets(trackResolved ? displayId : undefined, queryTrack, basePath)
  const localFacets = useMemo(() => computeFacetCounts(variants), [variants])
  // The facets result is only authoritative for the currently selected track.
  // After a track change the hook keeps the previous track's data for one frame
  // (it refetches in an effect), so gate every rendered field on the result's
  // own track matching queryTrack to avoid showing the prior track's counts and
  // TAG vocabulary (GEN-4325).
  const facetsMatchTrack = facetsTrack === queryTrack
  const usingServerFacets = serverFacets !== null && facetsMatchTrack
  const facets = usingServerFacets ? serverFacets.counts : localFacets
  const tagOrder = useMemo(() => (facetsMatchTrack ? serverTagOrder : []), [facetsMatchTrack, serverTagOrder])
  const tagDefinitions = useMemo(
    () => (facetsMatchTrack ? serverTagDefinitions : {}),
    [facetsMatchTrack, serverTagDefinitions]
  )
  // The selected track's TAG vocabulary is settled (a response landed for this
  // track), as opposed to still loading after a track change. This tells a
  // legitimately empty vocabulary (a track with no TAG field) apart from
  // "not ready yet," so the strip below can act in the former but not the latter.
  const vocabularyReady = facetsLoaded && facetsMatchTrack

  // Drop active TAG/FILTER filters that don't belong to the resolved track. Both
  // are server-side, per-track filters, so a shared/hand-edited URL like
  // ?track=cnv&tag=Confirmed or ?track=cnv&filter=X carrying a prior track's
  // value would query the CNV track for something it doesn't have and return an
  // empty table while the sidebar shows populated CNV counts (GEN-4325).
  // onTrackChange clears both on the interactive switch; this covers arriving
  // with an incompatible value already in the URL. The allowlists match what
  // FilterSidebar offers:
  //   - TAG: the header vocabulary (tagOrder, present even without a sidecar)
  //     plus, only when the counts are authoritative, the counted tags. Never
  //     the page-local counts, which can still be empty while variants load, so
  //     a valid-but-undeclared tag isn't stripped before its counts exist.
  //   - FILTER: has no header vocabulary, so it's pruned only against
  //     authoritative counts; left untouched until they exist.
  const serverTagKeys = useMemo(
    () => (usingServerFacets ? Object.keys(serverFacets.counts.tags) : []),
    [usingServerFacets, serverFacets]
  )
  const serverFilterKeys = useMemo(
    () => (usingServerFacets ? Object.keys(serverFacets.counts.filters) : []),
    [usingServerFacets, serverFacets]
  )
  useEffect(() => {
    if (!vocabularyReady) return
    const tagAllow = new Set([...tagOrder, ...serverTagKeys])
    const keptTags = filters.tags.filter((t) => tagAllow.has(t))
    // FILTER has no header vocabulary; only prune once counts are authoritative.
    const keptFilters = usingServerFacets
      ? filters.filters.filter((f) => serverFilterKeys.includes(f))
      : filters.filters
    if (keptTags.length !== filters.tags.length || keptFilters.length !== filters.filters.length) {
      updateUrl({ filters: { ...filters, tags: keptTags, filters: keptFilters } })
    }
  }, [vocabularyReady, usingServerFacets, tagOrder, serverTagKeys, serverFilterKeys, filters, updateUrl])

  // Free-text quick filter from the go-to box (client-side, not persisted).
  const [quickText, setQuickText] = useState('')
  const visible = useMemo(() => {
    const q = quickText.trim().toLowerCase()
    if (!q) return filteredSorted
    return filteredSorted.filter(
      (v) =>
        v.variant_id.toLowerCase().includes(q) ||
        (v.rsid?.toLowerCase().includes(q) ?? false) ||
        variantLocus(v).toLowerCase().includes(q)
    )
  }, [filteredSorted, quickText])

  // Selected variant: prefer a loaded row; fall back to a single-variant fetch
  // for deep links whose variant isn't in the loaded pages yet.
  const loadedSelected = useMemo(
    () =>
      filteredSorted.find((v) => v.variant_id === selectedVariantId) ??
      variants.find((v) => v.variant_id === selectedVariantId) ??
      null,
    [filteredSorted, variants, selectedVariantId]
  )
  // Result of the deep-link fetch for selectedVariantId when it isn't already in
  // the loaded table. Keyed by the id it was fetched for so a stale result from
  // a previous entry never lingers in the panel after a new (mistyped) entry is
  // submitted (GEN-3294). variant === null means a confirmed 404.
  const [fallback, setFallback] = useState<{ id: string; variant: Variant | null } | null>(null)
  useEffect(() => {
    // Wait for the track to be null or proven valid before deep-link fetching:
    // with a pending or stale ?track=, queryTrack is unset, so firing now would
    // hit the default VCF and could show the wrong record or a false 404 before
    // the track resolves (or before a stale value + selection are stripped).
    if (!selectedVariantId || loadedSelected || !displayId || !deepLinkTrackReady || !variantLookupPath) {
      setFallback(null)
      return
    }
    const abort = new AbortController()
    setFallback(null)
    const url = variantLookupPath(selectedVariantId, queryTrack)
    apiClient
      .get(url, undefined, true, abort.signal)
      .then(async (r) => (r.ok ? ((await r.json()) as Variant) : null))
      .then((v) => {
        if (abort.signal.aborted) return
        setFallback({ id: selectedVariantId, variant: v })
      })
      .catch(() => undefined)
    return () => abort.abort()
  }, [selectedVariantId, loadedSelected, displayId, apiClient, queryTrack, deepLinkTrackReady, variantLookupPath])

  // Only honor the fallback when it belongs to the current selection. The
  // instant selectedVariantId changes, a result fetched for a previous entry is
  // ignored, so a previously valid variant disappears immediately instead of
  // appearing to be the new (invalid) entry's result.
  const fallbackForSelection = fallback && fallback.id === selectedVariantId ? fallback : null
  const notFoundId = fallbackForSelection && fallbackForSelection.variant === null ? selectedVariantId : null
  const loadedOrFallback = loadedSelected ?? fallbackForSelection?.variant ?? null

  // review_metrics (LOH summary) is single-variant-only: the list endpoint does
  // not compute it (it needs a separate SNV scan), so a CNV row picked from the
  // table carries no metrics. Enrich such a selection with the /variants/{id}
  // record (which does carry review_metrics) and merge it onto the loaded row so
  // the LOH block appears during normal review, not just on deep-link fetches
  // (GEN-4448). Only CNV rows missing metrics trigger the fetch, so an SNV click
  // costs nothing. The deep-link fallback path already returns a full record, so
  // it needs no enrichment.
  const [metricsEnrichment, setMetricsEnrichment] = useState<{
    id: string
    review_metrics: Variant['review_metrics']
  } | null>(null)
  // Metrics we already have for the current selection without a *new* request:
  // the deep-link fallback record carries review_metrics, and a completed
  // enrichment for this id does too. Only `fallbackMetrics` (not the enrichment)
  // gates the fetch, so the effect never depends on its own output -- otherwise
  // storing the result would flip the guard and refetch in a loop. Reusing the
  // fallback also avoids a redundant /variants/{id} call (and the LOH block
  // flickering) when a deep-linked CNV's row later pages into the table and
  // becomes loadedSelected (Bugbot).
  const fallbackMetrics = fallbackForSelection?.variant?.review_metrics ?? null
  const enrichedMetrics =
    metricsEnrichment && metricsEnrichment.id === selectedVariantId ? metricsEnrichment.review_metrics : null
  const availableMetrics = enrichedMetrics ?? fallbackMetrics
  const needsMetrics =
    loadedSelected !== null && isCnv(loadedSelected) && loadedSelected.review_metrics == null && fallbackMetrics == null
  useEffect(() => {
    if (!needsMetrics || !selectedVariantId || !displayId || !deepLinkTrackReady || !variantLookupPath) return
    const abort = new AbortController()
    const url = variantLookupPath(selectedVariantId, queryTrack)
    apiClient
      .get(url, undefined, true, abort.signal)
      .then(async (r) => (r.ok ? ((await r.json()) as Variant) : null))
      .then((v) => {
        if (abort.signal.aborted || !v) return
        setMetricsEnrichment({ id: selectedVariantId, review_metrics: v.review_metrics ?? null })
      })
      .catch(() => undefined)
    return () => abort.abort()
  }, [needsMetrics, selectedVariantId, displayId, apiClient, queryTrack, deepLinkTrackReady, variantLookupPath])

  // When the loaded row lacks review_metrics, merge in whatever metrics we
  // already have for this selection (enrichment result or the deep-link
  // fallback record). Otherwise use the record as-is.
  const selectedVariant = useMemo(() => {
    if (loadedSelected && loadedSelected.review_metrics == null && availableMetrics != null) {
      return { ...loadedSelected, review_metrics: availableMetrics }
    }
    return loadedOrFallback
  }, [loadedSelected, loadedOrFallback, availableMetrics])

  // Drop a stale ?variant= deep link that resolved on a different track than the
  // one selected. Single-variant lookup is cross-track (GEN-3792), so a shared or
  // back/forward URL like ?variant-track=cnv&variant=<an SNV id> would otherwise
  // pin that SNV in the panel while the user reviews CNV. The API reports which
  // track the locus matched (matched_track, GEN-4350); when it differs from the
  // selected track we clear the selection, mirroring onTrackChange's clear on an
  // interactive switch. Only the cross-track *fallback* fetch carries
  // matched_track (a loaded row already belongs to the current track), and we
  // skip it while a go-to scope is active so an in-session lookup still surfaces.
  const fallbackMatchedTrack = fallbackForSelection?.variant?.matched_track ?? null
  useEffect(() => {
    if (!deepLinkTrackReady || gotoRegion || !selectedTrack) return
    if (fallbackMatchedTrack && fallbackMatchedTrack !== selectedTrack) {
      updateUrl({ selectedVariantId: null })
    }
  }, [deepLinkTrackReady, gotoRegion, selectedTrack, fallbackMatchedTrack, updateUrl])

  // Navigate IGV to a deep-linked variant once its record resolves (GEN-5722).
  // A ?variant= URL sets the selection and the ROI highlight but, unlike the
  // go-to box / open-in-IGV, never moved the viewer, so a shared link opened on
  // the default first-entry locus (GEN-5721) instead of the linked variant. We
  // scope this strictly to the URL-originated selection present on load: latch
  // that id at mount, then the first time it resolves to a concrete record and
  // the locus is still unset, jump to its ROI locus (full CNV interval, or
  // chr:pos for SNVs - matching the highlight box). Fires once and never
  // re-navigates later manual selections, which stay highlight-only as before.
  const deepLinkVariantIdRef = useRef<string | null>(selectedVariantId)
  const didDeepLinkNavigateRef = useRef(false)
  useEffect(() => {
    if (didDeepLinkNavigateRef.current) return
    const deepLinkId = deepLinkVariantIdRef.current
    if (!deepLinkId || igvLocus !== undefined) return
    // Only act on the deep-linked id, and only once it resolves to a record.
    if (selectedVariantId !== deepLinkId || !selectedVariant) return
    // Don't navigate a cross-track deep link that is about to be cleared: a
    // fallback record whose matched_track differs from the active track is
    // rejected by the clear effect above, and jumping to it would move the
    // viewer to a locus that should never show and burn the one-shot latch
    // (Bugbot). If it is later re-resolved on the right track, we navigate then.
    if (fallbackMatchedTrack && fallbackMatchedTrack !== selectedTrack) return
    didDeepLinkNavigateRef.current = true
    setIgvLocus(variantRoiLocus(selectedVariant))
  }, [selectedVariantId, selectedVariant, igvLocus, fallbackMatchedTrack, selectedTrack])

  // After a locus/interval go-to scopes the table, auto-select the first
  // variant in the region once it loads so the detail panel populates. Cleared
  // once handled (or if the region is empty); manual clicks cancel it too.
  useEffect(() => {
    if (!pendingLocusSelect || loading || initialLoading) return
    if (variants.length > 0) updateUrl({ selectedVariantId: variants[0].variant_id })
    setPendingLocusSelect(false)
  }, [pendingLocusSelect, loading, initialLoading, variants, updateUrl])

  // A navigated variant/locus that isn't present in the case: either a deep-link
  // variant id that 404'd, or a go-to locus that scoped the table to an empty
  // region. Drives the "not found" error panel below. We intentionally do not
  // suggest a nearby variant: variant entry is a manual copy-paste step, so an
  // entry must match exactly or it is an error (GEN-3294).
  const missingTarget = useMemo(() => {
    if (selectedVariant) return null
    if (notFoundId) return { label: notFoundId }
    if (gotoRegion && !loading && !initialLoading && variants.length === 0) {
      return { label: igvLocus ?? gotoRegion }
    }
    return null
  }, [selectedVariant, notFoundId, gotoRegion, loading, initialLoading, variants.length, igvLocus])

  const onSortChange = useCallback(
    (key: SortKey) => {
      const dir = sort.key === key && sort.dir === 'asc' ? 'desc' : 'asc'
      updateUrl({ sort: { key, dir } })
    },
    [sort, updateUrl]
  )

  // A manual row click cancels any pending locus auto-select.
  const onSelect = useCallback(
    (variantId: string) => {
      setPendingLocusSelect(false)
      updateUrl({ selectedVariantId: variantId })
    },
    [updateUrl]
  )
  // Touching the sidebar filters drops the locus scope so the chrom/full view
  // (and its facets) takes over again.
  const onFilters = useCallback(
    (next: VariantFilters) => {
      setGotoRegion(null)
      setPendingLocusSelect(false)
      updateUrl({ filters: next })
    },
    [updateUrl]
  )
  const onReset = useCallback(() => {
    setGotoRegion(null)
    setPendingLocusSelect(false)
    updateUrl({ filters: emptyFilters() })
  }, [updateUrl])

  // Switching the variant track changes which VCF the table reads, so drop the
  // current selection and locus scope (variant ids and loci differ per track),
  // and clear the server-side, per-track filter vocabularies (TAG/FILTER) so a
  // tag valid only on the previous track can't keep the new track's table empty
  // while its facets show populated counts (numeric/chrom filters are kept).
  // Selecting the default clears ?track= to keep the URL clean.
  const onTrackChange = useCallback(
    (next: string) => {
      setGotoRegion(null)
      setPendingLocusSelect(false)
      updateUrl({
        track: next === defaultTrackId ? null : next,
        selectedVariantId: null,
        filters: { ...filters, tags: [], filters: [] },
      })
    },
    [updateUrl, defaultTrackId, filters]
  )

  // Clear just the go-to locus scope, returning the table to the chrom/full view.
  const clearLocusScope = useCallback(() => {
    setGotoRegion(null)
    setPendingLocusSelect(false)
  }, [])
  const openInIgv = useCallback((locus: string) => setIgvLocus(locus), [])

  // On first entry, land IGV on a real locus instead of the whole-genome "all
  // chromosomes" view (GEN-5721). At whole-genome the alignment tracks sit above
  // their visibility window so reads never draw, and IGV's single-locus zoom
  // control isn't presented; the genome-wide wig tracks still render, which
  // masks the zoomed-out state. Land on the first variant's position (a point
  // locus, well under ALIGNMENT_VISIBILITY_WINDOW_BP, so reads load); fall back
  // to chr1 when the case has no variants. Fires once and never overrides a
  // go-to scope or a deep-linked selection, which set the locus themselves.
  const didInitLocusRef = useRef(false)
  useEffect(() => {
    if (didInitLocusRef.current || igvLocus !== undefined) return
    // A go-to scope owns the locus; don't pre-empt it (this is terminal here -
    // the go-to path always sets igvLocus, so we can stand down for good).
    if (gotoRegion) {
      didInitLocusRef.current = true
      return
    }
    // A deep link owns the locus *while it is still resolving*. Stand down
    // WITHOUT latching so that if the deep link never yields a locus - a
    // cross-track/unknown ?variant= that gets cleared, or a confirmed 404 - this
    // effect can still fall through and apply the default, rather than leaving
    // the viewer stuck on whole-genome (Bugbot). It is "still resolving" when a
    // selection id is present that is neither confirmed-not-found nor a
    // cross-track record about to be discarded.
    //
    // The cross-track test needs a populated selectedTrack, which is '' both
    // while the variant-tracks list loads and once it resolves EMPTY or errors
    // (defaultTrackId is null). The earlier fix gated on selectedTrack !== '',
    // which could not tell those apart, so a resolved, non-404 deep link on an
    // empty/failed track list stayed pending forever and IGV never left whole-
    // genome (GEN-5737). Use trackResolved to distinguish "list still loading"
    // (keep pending) from "list settled empty" (a record that resolved here has
    // no track to reject against and will not navigate, so fall through).
    const crossTrackRejected = selectedTrack !== '' && !!fallbackMatchedTrack && fallbackMatchedTrack !== selectedTrack
    // An empty/errored track list has settled with selectedTrack '' (no
    // defaultTrackId). A record that resolved via the cross-track FALLBACK fetch
    // carries a non-null matched_track, so the deep-link nav effect bails
    // (matched_track !== '') and would never move the viewer; stop holding so
    // the default latches. Gate on fallbackMatchedTrack, not just any resolved
    // record: the happy path where the deep-linked variant is a loaded row has a
    // null matched_track and still navigates to its own locus, so it must stay
    // pending here and not be pre-empted by the default (GEN-5737).
    //
    // Use !variantTracksLoading, not trackResolved, for the "list settled"
    // signal: trackResolved is true the moment the URL has no ?track=, even
    // while the list is still loading, so with a fallback matched_track and a
    // first variants page that settles first, this guard could fire mid-load
    // and latch the default before the deep link navigates (GEN-5761). An
    // empty/errored list still ends with variantTracksLoading false and
    // selectedTrack '', so the GEN-5737 fall-through is preserved.
    const settledEmptyFallback = !variantTracksLoading && selectedTrack === '' && !!fallbackMatchedTrack
    const deepLinkPending =
      !!selectedVariantId && notFoundId !== selectedVariantId && !crossTrackRejected && !settledEmptyFallback
    if (deepLinkPending) return
    // Wait until the first page of variants has settled before choosing.
    if (initialLoading) return
    didInitLocusRef.current = true
    setIgvLocus(variants.length > 0 ? variantLocus(variants[0]) : 'chr1')
  }, [
    igvLocus,
    selectedVariantId,
    gotoRegion,
    initialLoading,
    variants,
    notFoundId,
    fallbackMatchedTrack,
    selectedTrack,
    variantTracksLoading,
  ])

  // Shared drag logic for the two column dividers. `sign` is +1 for the left
  // divider (drag right => wider) and -1 for the right divider (drag left =>
  // wider). Pointer capture keeps the drag alive when the cursor leaves the
  // thin handle; the width is clamped to [min, max].
  const startDividerDrag = (
    e: React.PointerEvent<HTMLDivElement>,
    opts: { startWidth: number; sign: 1 | -1; min: number; max: number; set: (w: number) => void }
  ) => {
    e.preventDefault()
    const startX = e.clientX
    e.currentTarget.setPointerCapture?.(e.pointerId)
    const onMove = (ev: PointerEvent) => {
      const next = opts.startWidth + opts.sign * (ev.clientX - startX)
      opts.set(Math.min(opts.max, Math.max(opts.min, next)))
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  const onLeftDividerPointerDown = (e: React.PointerEvent<HTMLDivElement>) =>
    startDividerDrag(e, { startWidth: leftWidth, sign: 1, min: LEFT_PANEL_MIN, max: LEFT_PANEL_MAX, set: setLeftWidth })
  const onRightDividerPointerDown = (e: React.PointerEvent<HTMLDivElement>) =>
    startDividerDrag(e, {
      startWidth: rightWidth,
      sign: -1,
      min: RIGHT_PANEL_MIN,
      max: RIGHT_PANEL_MAX,
      set: setRightWidth,
    })

  // Go-to box: jump table selection + IGV for a locus/variant/rsID; free text
  // narrows the table instead.
  const onGoTo = useCallback(
    (result: GoToResult) => {
      if (result.kind === 'text') {
        setQuickText(result.value)
        return
      }
      setQuickText('')
      if (result.kind === 'locus') {
        setIgvLocus(result.value)
        // Scope the table to this locus/interval (a single position scopes to
        // that exact base) and auto-select its first variant once the region
        // loads, so the detail panel fills in.
        const scoped = locusToRegion(result.value)
        if (scoped) {
          setGotoRegion(scoped)
          setPendingLocusSelect(true)
          updateUrl({ selectedVariantId: null })
        }
      } else if (result.kind === 'variantId') {
        // Scope the table to the variant's exact locus (like the locus path) so
        // its row surfaces in the list instead of staying buried on a far page,
        // then select that exact variant.
        const loc = variantIdToLocus(result.value)
        if (loc) {
          setIgvLocus(loc)
          const scoped = locusToRegion(loc)
          if (scoped) setGotoRegion(scoped)
        }
        setPendingLocusSelect(false)
        updateUrl({ selectedVariantId: result.value })
      } else {
        const match = variants.find((v) => v.rsid?.toLowerCase() === result.value)
        if (match) {
          updateUrl({ selectedVariantId: match.variant_id })
          setIgvLocus(variantLocus(match))
        } else {
          setQuickText(result.value)
        }
      }
    },
    [updateUrl, variants]
  )

  const [copied, setCopied] = useState(false)
  const copyShareLink = useCallback(() => {
    void navigator.clipboard?.writeText(window.location.href).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1200)
    })
  }, [])

  if (!displayId) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-8">
        <p className="text-text-subtle text-sm">No {titleKicker.toLowerCase()} ID provided.</p>
        <Link to={backTo} className="text-brand-700 text-sm hover:underline">
          {backLabel}
        </Link>
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col">
      {/* tgg-style layout (GEN-3129): filters left, IGV the dominant center
          column at full viewport height (room for ~6 BAM/CRAM tracks, which
          scroll within the column), then a draggable divider and the variant
          table on the right with the detail panel stacked below it when a
          variant is selected. The center flexes to fill the full window width. */}
      <div
        className="grid min-h-0 min-w-0 flex-1 overflow-x-auto"
        data-testid="case-workspace-grid"
        style={{ gridTemplateColumns: `${leftWidth}px 12px minmax(${IGV_MIN_WIDTH}px,1fr) 12px ${rightWidth}px` }}
      >
        <VScrollArea className="border-border-subtle border-r" data-testid="case-page-sidebar-scroll">
          <div className="border-border-subtle border-b p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <span className="text-text-subtle text-[10px] font-medium tracking-wider uppercase">{titleKicker}</span>
                <h1
                  data-testid="case-page-title"
                  className="text-text-heading truncate font-mono text-lg font-semibold tracking-tight"
                >
                  {displayId}
                </h1>
                {subtitle && (
                  <span
                    data-testid="case-page-subtitle"
                    className="border-border-subtle text-text-subtle mt-1 inline-block rounded-full border px-2 py-0.5 text-[10px] font-medium"
                  >
                    {subtitle}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={copyShareLink}
                className="border-border-subtle text-text-subtle hover:text-text-heading shrink-0 rounded border px-2 py-1 text-[11px]"
                data-testid="copy-share-link"
              >
                {copied ? 'Copied' : 'Copy link'}
              </button>
            </div>
            <p className="text-text-subtle mt-1 text-xs" data-testid="case-page-variant-count">
              {visible.length} / {variants.length} variants{hasMore ? '+' : ''}
            </p>
            {variantTracks.length > 1 && (
              <div className="mt-3">
                <label
                  htmlFor="variant-track-select"
                  className="text-text-subtle text-[10px] font-medium tracking-wider uppercase"
                >
                  Variant track
                </label>
                <select
                  id="variant-track-select"
                  data-testid="variant-track-select"
                  value={selectedTrack}
                  onChange={(e) => onTrackChange(e.target.value)}
                  className="border-border-subtle text-text-heading focus:border-brand-600 focus:ring-brand-600/20 mt-1 w-full rounded border bg-white px-2 py-1 text-xs focus:ring-2 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                >
                  {variantTracks.map((t) => (
                    <option key={t.track_id} value={t.track_id}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <LocusListPanel caseId={displayId} onGo={onGoTo} onLiveText={setQuickText} />
          <FilterSidebar
            filters={filters}
            facets={facets}
            tagOrder={tagOrder}
            tagDefinitions={tagDefinitions}
            onChange={onFilters}
            onReset={onReset}
            partial={hasMore}
            authoritative={usingServerFacets}
          />
        </VScrollArea>

        <ColumnDivider
          onPointerDown={onLeftDividerPointerDown}
          testId="filter-panel-divider"
          label="Resize the filter panel"
        />

        <div className="border-border-subtle flex min-h-0 flex-col border-r p-2" data-testid="case-page-igv-region">
          {tracksError && (
            <p role="alert" className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-800">
              IGV: {tracksError}
            </p>
          )}
          {tracksLoading && !tracksError && <p className="text-text-subtle p-2 text-xs">Loading IGV tracks...</p>}
          {igvTracks && (
            <>
              <div className="relative mb-1 flex items-center gap-3 px-1">
                <label className="text-text-subtle flex items-center gap-1 text-xs" htmlFor="igv-toggle-genes">
                  <input
                    id="igv-toggle-genes"
                    type="checkbox"
                    checked={showGenes}
                    onChange={(e) => setShowGenes(e.target.checked)}
                    data-testid="igv-toggle-genes"
                  />
                  RefSeq genes
                </label>
                <label className="text-text-subtle flex items-center gap-1 text-xs" htmlFor="igv-toggle-segdup">
                  <input
                    id="igv-toggle-segdup"
                    type="checkbox"
                    checked={showSegdup}
                    onChange={(e) => setShowSegdup(e.target.checked)}
                    data-testid="igv-toggle-segdup"
                  />
                  Segmental dups
                </label>
                <button
                  type="button"
                  onClick={() => setSettingsOpen((o) => !o)}
                  className="border-border-subtle text-text-subtle hover:text-text-heading rounded border px-2 py-0.5 text-xs"
                  aria-expanded={settingsOpen}
                  data-testid="igv-settings-toggle"
                >
                  Display options
                </button>
                {settingsOpen && (
                  <div
                    className="border-border-subtle absolute top-7 left-1 z-10 flex flex-col gap-1 rounded border bg-white p-2 text-xs shadow"
                    data-testid="igv-settings-popover"
                  >
                    {(
                      [
                        ['vcfExpanded', 'VCF expanded'],
                        ['showSoftClips', 'Show soft clips'],
                        ['shadeByStrand', 'Shade reads by strand'],
                        ['colorByInsertSize', 'Color by insert size / pair orientation'],
                        ['viewAsPairs', 'View reads as pairs'],
                        ['bafHetOnly', 'BAF: het-only (hide 0/1 rails)'],
                      ] as Array<[keyof IgvDisplaySettings, string]>
                    ).map(([key, label]) => (
                      <label key={key} className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={igvSettings[key]}
                          onChange={(e) => setIgvSettings((s) => ({ ...s, [key]: e.target.checked }))}
                          data-testid={`igv-opt-${key}`}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="igv-scroll min-h-0 flex-1 overflow-auto">
                <IgvMount
                  tracks={igvTracks}
                  genome="hg38"
                  reference={igvReference}
                  locus={igvLocus}
                  onError={onIgvError}
                  dragZoomWigTracks
                  roiLocus={selectedVariant ? variantRoiLocus(selectedVariant) : null}
                  reloadKey={`${displayId}:${showGenes ? 'g' : 'x'}${showSegdup ? 's' : 'x'}:${igvSettings.viewAsPairs ? 1 : 0}${igvSettings.showSoftClips ? 1 : 0}${igvSettings.shadeByStrand ? 1 : 0}${igvSettings.colorByInsertSize ? 1 : 0}${igvSettings.vcfExpanded ? 1 : 0}${igvSettings.bafHetOnly ? 1 : 0}`}
                  data-testid="case-page-igv"
                  className="h-full"
                  hostClassName="min-h-full"
                />
              </div>
            </>
          )}
        </div>

        <ColumnDivider
          onPointerDown={onRightDividerPointerDown}
          testId="igv-panel-divider"
          label="Resize the variant panel"
        />

        <div className="border-border-subtle flex min-h-0 flex-col border-l">
          <ActiveFilterChips filters={filters} onChange={onFilters} onReset={onReset} />
          {gotoRegion && (
            <div
              className="border-border-subtle text-text-subtle flex items-center justify-between gap-2 border-b px-2 py-1 text-[11px]"
              data-testid="locus-scope-banner"
            >
              <span className="truncate">
                Showing <span className="text-text-heading font-mono">{igvLocus ?? gotoRegion}</span>
              </span>
              <button
                type="button"
                onClick={clearLocusScope}
                className="text-brand-700 shrink-0 hover:underline"
                data-testid="locus-scope-clear"
              >
                Clear
              </button>
            </div>
          )}
          <div className="min-h-0 flex-1">
            {error ? (
              <div
                role="alert"
                data-testid="case-page-error"
                className="m-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"
              >
                <p className="font-medium">Could not load variants</p>
                <p className="mt-1">{error}</p>
              </div>
            ) : initialLoading ? (
              <p className="text-text-subtle p-4 text-sm" data-testid="case-page-status">
                Loading variants...
              </p>
            ) : (
              <VariantTable
                variants={visible}
                selectedVariantId={selectedVariantId}
                onSelect={onSelect}
                onOpenInIgv={openInIgv}
                sort={sort}
                onSortChange={onSortChange}
                hasMore={hasMore}
                loading={loading}
                onLoadMore={loadMore}
              />
            )}
          </div>

          {selectedVariant ? (
            <VScrollArea
              className="border-border-subtle min-h-0 shrink-0 basis-2/5 border-t"
              data-testid="case-page-detail-scroll"
            >
              <VariantDetailPanel variant={selectedVariant} onOpenInIgv={openInIgv} />
            </VScrollArea>
          ) : missingTarget ? (
            <div className="border-border-subtle min-h-0 shrink-0 basis-2/5 overflow-auto border-t">
              <VariantNotFoundPanel label={missingTarget.label} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default CaseWorkspace
